import { useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Loader } from "@mantine/core";
import { IconWallet, IconAlertCircle, IconArrowLeft } from "@tabler/icons-react";
import { toast } from "sonner";
import { CustomerOrganization } from "../../utils/interfaces";
import {
  useCustomerPaymentsQuery,
  useCustomerSummaryQuery,
  useCustomerRepresentativeQuery,
  useBlockCustomerMutation,
} from "../../api/queries/customerOrganizationQueries";
import { useAuth } from "../../hooks/useAuth";
import { ConfirmationModal } from "../ConfirmationModal";
import { CustomerContactsSection } from "./CustomerContactsSection";
import { CustomerEscalationAgentSection } from "./CustomerEscalationAgentSection";
import { CustomerDescriptionSection } from "./CustomerDescriptionSection";
import { CustomerInvoicesTable } from "./CustomerInvoicesTable";
import { displayCurrencyAmount } from "../../utils/display";

interface CustomerOrganizationDetailProps {
  customerId?: number;
  panelMode?: boolean;
  embedded?: boolean;
  onClosePanel?: () => void;
  onOpenInvoice?: (externalId: string) => void;
}

export const CustomerOrganizationDetail = ({
  customerId: customerIdProp,
  panelMode = false,
  embedded = false,
  onClosePanel,
  onOpenInvoice,
}: CustomerOrganizationDetailProps = {}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isOrgManager } = useAuth();
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [showPaidInvoices, setShowPaidInvoices] = useState(false);
  const [queriesEnabled, setQueriesEnabled] = useState(true);
  const blockCustomerMutation = useBlockCustomerMutation();
  const { customerId: customerIdParam } = useParams<{ customerId: string }>();
  const customerIdFromRoute = customerIdParam
    ? parseInt(customerIdParam, 10)
    : undefined;
  const customerId = customerIdProp ?? customerIdFromRoute;

  const initialCustomer = (location.state as { customer?: CustomerOrganization })
    ?.customer;

  const effectiveCustomerId = customerId || initialCustomer?.id;

  const {
    data: fetchedCustomer,
    isLoading: isCustomerLoading,
    refetch: refetchCustomer,
  } = useCustomerRepresentativeQuery(effectiveCustomerId, {
    enabled: queriesEnabled && !!effectiveCustomerId,
    staleTime: 0,
  });

  const customer =
    fetchedCustomer || (isCustomerLoading ? undefined : initialCustomer);

  const {
    data: payments,
    isLoading: isPaymentsLoading,
    isFetching: isPaymentsFetching,
  } = useCustomerPaymentsQuery(customer?.id, {
    enabled: queriesEnabled && !!customer?.id,
    includePaid: showPaidInvoices,
    placeholderData: (previous) => previous,
  });
  const { data: summary } = useCustomerSummaryQuery(customer?.id, {
    enabled: queriesEnabled && !!customer?.id,
  });

  const openBalance = summary?.total || 0;
  const overdue = summary
    ? Math.max(0, summary.total - summary.notOverdueAmount)
    : 0;
  const currency = summary?.currency || "USD";

  const goBack = () => {
    if (panelMode && onClosePanel) {
      onClosePanel();
      return;
    }
    navigate(-1);
  };

  const handleBlockCustomer = async () => {
    if (!customer?.id) return;
    setQueriesEnabled(false);
    try {
      await blockCustomerMutation.mutateAsync(customer.id);
      toast.success("Customer blocked");
      setShowBlockModal(false);
      if (panelMode && onClosePanel) {
        onClosePanel();
        return;
      }
      navigate("/receivables", { replace: true });
    } catch (error: unknown) {
      setQueriesEnabled(true);
      const message =
        (error as { response?: { data?: { detail?: { message?: string } } } })
          ?.response?.data?.detail?.message || "Failed to block customer";
      toast.error(message);
    }
  };

  if (isCustomerLoading && !customer) {
    return (
      <div className="flex h-56 items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="flex h-56 items-center justify-center text-sm text-slate-600">
        Customer not found
      </div>
    );
  }

  return (
    <div
      className={`mx-auto flex w-full flex-col gap-3 p-4 pb-10 ${
        panelMode ? "max-w-5xl" : "max-w-6xl"
      }`}
    >
      <header className="flex items-center gap-3">
        {!embedded ? (
          <button
            type="button"
            onClick={goBack}
            aria-label="Go back"
            className="shrink-0 rounded-lg border border-slate-200 bg-white p-2 text-slate-700 shadow-sm hover:bg-slate-50"
          >
            <IconArrowLeft size={20} />
          </button>
        ) : null}
        <h1 className="min-w-0 flex-1 truncate text-xl font-semibold text-slate-900 sm:text-2xl">
          {customer.customer_organization_name}
        </h1>
        {isOrgManager && (
          <button
            type="button"
            onClick={() => setShowBlockModal(true)}
            className="shrink-0 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-100"
          >
            Block customer
          </button>
        )}
      </header>

      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
        <div className="flex justify-between rounded-xl border border-blue-100 bg-blue-50 p-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500">Open Balance</p>
            <p className="mt-1 text-2xl font-thin text-slate-900">
              {displayCurrencyAmount(openBalance, currency)}
            </p>
          </div>
          <div className="h-min self-center rounded-lg border border-blue-300 bg-white p-1.5">
            <IconWallet className="text-blue-500" size={20} />
          </div>
        </div>
        <div className="flex justify-between rounded-xl border border-rose-100 bg-rose-50 p-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500">Overdue</p>
            <p className="mt-1 text-2xl font-thin text-slate-900">
              {displayCurrencyAmount(overdue, currency)}
            </p>
          </div>
          <div className="h-min self-center rounded-lg border border-red-300 bg-white p-1.5">
            <IconAlertCircle className="text-red-500" size={20} />
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
        <div className="space-y-3 p-3">
          <CustomerContactsSection
            customer={customer}
            onCustomerUpdate={() => refetchCustomer()}
          />
          <CustomerEscalationAgentSection customer={customer} />
          <CustomerDescriptionSection
            customer={customer}
            onCustomerUpdate={() => refetchCustomer()}
            queriesEnabled={queriesEnabled}
          />
        </div>
      </div>

      <CustomerInvoicesTable
        payments={payments}
        isLoading={isPaymentsLoading && !payments}
        isFetching={isPaymentsFetching}
        showPaidInvoices={showPaidInvoices}
        onShowPaidInvoicesChange={setShowPaidInvoices}
        onOpenInvoice={onOpenInvoice}
      />

      <ConfirmationModal
        open={showBlockModal}
        title="Block this customer?"
        description="This permanently deletes invoices, emails, facts, contacts, and notes for this customer in Budy. The customer will be hidden until a manager unblocks them in Settings."
        confirmLabel="Block customer"
        isLoading={blockCustomerMutation.isPending}
        onCancel={() => setShowBlockModal(false)}
        onConfirm={handleBlockCustomer}
      />
    </div>
  );
};

/** @deprecated Use CustomerOrganizationDetail */
export const CustomerDetail = CustomerOrganizationDetail;
